/* eslint-disable @typescript-eslint/no-explicit-any */
import { DragSource } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { oc } from 'ts-optchain';
import clsx from 'clsx';

import { DndItemTypes } from 'constants/dndItemTypes';
import { GRID_SIZE, getDefaultLocation } from 'constants/AerialConstants';
import AerialTable, { Props as AerialTableProps } from 'components/aerial/AerialTable';
import AerialTableEditPopover from 'components/settings/tables/edit-popover/AerialTableEditPopover';
import PureBaseComponent from '@tock/shared/components/PureBaseComponent';

const settingsAerialTableSource = {
  beginDrag(props: Props) {
    return {
      location: props.location || getDefaultLocation(), // Put the default location in so that we can drag even a table w/o a location
      id: props.id,
      name: props.name,
      size: props.size,
    };
  },
  canDrag(props: Props) {
    return props.isEditing && !props.isSelectedTable;
  },
};

function collectSource(connect: any, monitor: any) {
  return {
    connectDragSource: connect.dragSource(),
    connectDragPreview: connect.dragPreview(),
    isDragging: monitor.isDragging(),
  };
}

interface Props extends AerialTableProps {
  connectDragPreview?(preview: any, options: any): void;
  connectDragSource?(node: JSX.Element): JSX.Element;
  hasCollision: boolean;
  isEditing: boolean;
  onDeselectTable(tableId: number): void;
  onResetTableLocation(tableId: number): void;
  onUpdateTableLocation(tableId: number, tableLocation: TableLocation): void;
}

class SettingsAerialTable extends PureBaseComponent<Props> {
  static defaultProps: any;

  componentDidMount() {
    if (this.props.connectDragPreview) {
      // Use empty image as a drag preview so browsers don't draw it
      // and we can draw whatever we want on the custom drag layer instead.
      this.props.connectDragPreview(getEmptyImage(), {
        // IE fallback: specify that we'd rather screenshot the node
        // when it already knows it's being dragged so we can hide it with CSS.
        captureDraggingState: true,
      });
    }
  }

  getStyle() {
    if (this.props.isDragPreview) {
      return {
        left: `${oc(document.documentElement).scrollLeft()}px`,
        top: `${oc(document.documentElement).scrollTop()}px`,
      };
    }
    const location = this.props.location as TableLocation;
    return {
      left: `${GRID_SIZE * location.xPosition}px`,
      top: `${GRID_SIZE * location.yPosition}px`,
    };
  }

  render() {
    const location = this.props.location as TableLocation;
    const isEditingThisTable = this.props.isEditing && this.props.isSelectedTable;
    const connectDragSource = this.props.connectDragSource || ((node: JSX.Element) => node);
    const className = clsx(
      'SettingsAerialTable',
      `SettingsAerialTable--visualSize${location.visualSize}`
    );
    return connectDragSource(
      <div className={className} style={this.getStyle()}>
        <AerialTable {...this.props} hasError={this.props.hasCollision && this.props.isEditing} />
        {isEditingThisTable && (
          <AerialTableEditPopover
            id={this.props.id}
            location={location}
            name={this.props.name}
            onClose={this.props.onDeselectTable}
            onResetTableLocation={this.props.onResetTableLocation}
            onUpdateTableLocation={this.props.onUpdateTableLocation}
          />
        )}
      </div>
    );
  }
}

SettingsAerialTable.defaultProps = {
  location: getDefaultLocation(),
};

export default DragSource(
  DndItemTypes.SETTINGS_AERIAL_TABLE,
  settingsAerialTableSource,
  collectSource
)(SettingsAerialTable);
